import React, {
  useEffect,
  useState,
} from 'react';
import {
  RootStateOrAny,
  useSelector,
} from 'react-redux';
import {
  AddIcon,
  SearchIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Flex,
  Heading,
  Stack,
  InputGroup,
  Input,
  InputLeftElement,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import { useDebounce } from 'use-debounce';

import authModule from 'shared/src/modules/auth';
import { useQuerystringParam } from 'shared/src/hooks/useQuerystringParam';

import User, { Role } from 'web-react-ui/src/types/User.interface';
import Location from 'web-react-ui/src/types/Location.interface';
import RoleFilter from 'web-react-ui/src/chakra/UserList/RoleFilter';
import UserListView from 'web-react-ui/src/chakra/users/UserListView';
import AddUserView from 'web-react-ui/src/chakra/users/AddUserView';

import businessModule from '../../../modules/businesses';
import propertyModule from '../../../modules/property';
import client from '../../../services/client';
import confirm from '../../../services/confirm';

const BusinessUsers = () => {
  const currentUser = useSelector((state: RootStateOrAny) => authModule.selectors.getUser(state));
  const property = useSelector((state: RootStateOrAny) => propertyModule.selectors.property.getData(state));
  const business = useSelector((state: RootStateOrAny) => businessModule.selectors.business.getData(state));

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [refetchId, setRefetchId] = useState(0);
  const [isInviting, setIsInviting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [operationError, setOperationError] = useState('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [query, setQuery] = useQuerystringParam('query', '', { squash: true });
  const [debouncedQuery] = useDebounce(query, 750);
  const [selectedRoles, setSelectedRoles] = useQuerystringParam(
    'roles',
    '',
    {
      transport: {
        serialize: (v: []) => v && v.join(','),
        parse: (v: string) => v && v.split(','),
      },
      squash: true,
    });

  const loadUsers = () => client.properties.for(property.id).businesses.for(business.id).users.searchUsers({
    query: debouncedQuery || undefined,
    roleIds: selectedRoles || undefined,
    context: ['PB', 'PBL'],
  });

  const loadLocations = () => client
    .properties.for(property.id)
    .businesses.for(business.id)
    .locations.listAdmin();

  useEffect(() => {
    const run = async () => {
      const locs = await loadLocations();
      setLocations(locs.items);
    };

    run();
  }, [property, business]);

  useEffect(() => {
    const run = async () => {
      const bizRoles = await client.roles.list({ type: 'property-business' });
      const locRoles = await client.roles.list({ type: 'property-business-location' });
      setRoles([...bizRoles.items, ...locRoles.items]);
    };

    run();
  }, []);

  const addUserSubmit = async (values: Record<string, any>) => {
    setIsInviting(true);
    setOperationError('');
    const { email, name, roles: businessRoles = [], locationRoles = [] } = values;

    if (!businessRoles?.length && !Object.entries(locationRoles).length) {
      setIsInviting(false);
      setOperationError('No roles selected');
      return;
    }

    const runLocationRequests = async (previous: any, entry: any) => {
      const [locationId, role] = entry;
      await previous;

      return client
        .properties.for(property.id)
        .businesses.for(business.id)
        .locations.for(locationId)
        .users.invite({ email, name }, { id: role[0].id }, true);
    };

    try {
      if (businessRoles.length > 0) {
        await client
          .properties.for(property.id)
          .businesses.for(business.id)
          .users.invite(
            { email, name },
            businessRoles.map((item: string) => ({ id: item })),
            true,
          );
      }
      if (Object.entries(locationRoles).length > 0) {
        // locations only have one role per location per user, so still need to be run sequentially
        await Object.entries(locationRoles || {})?.reduce(runLocationRequests, Promise.resolve());
      }

      onClose();
      setRefetchId(refetchId + 1);
    } catch (error: any) {
      setOperationError(error);
      throw new Error(error);
    } finally {
      setIsInviting(false);
    }
  };

  const removeUser = async (user: User) => {
    const shouldDelete = confirm(`Are you sure you want to delete ${user.email}?`);
    if (!shouldDelete) return null;
    setIsDeleting(true);
    setOperationError('');

    try {
      await client
        .properties.for(property.id)
        .businesses.for(business.id)
        .users.for(user.id)
        .remove({ recursive: true });
    } catch (error: any) {
      setOperationError(error);
    } finally {
      setIsDeleting(false);
      setRefetchId(refetchId + 1);
    }

    return true;
  };

  return (
    <Box maxW="7xl" mx="auto" px={{ base: 2, md: 0 }}>
      <AddUserView
        isOpen={isOpen}
        onClose={onClose}
        type="business"
        roleSectionHeading="Business &amp; Location Roles"
        primaryRoles={roles.filter(role => role.type === 'property-business')}
        secondaryRoles={roles.filter(role => role.type === 'property-business-location')}
        roleTypes={['Business Roles', 'Location Roles']}
        locations={locations}
        onSubmit={addUserSubmit}
        isLoading={isInviting}
        error={operationError}
      />
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify={{ base: 'flex-start', md: 'space-between' }}
        align={{ base: 'flex-start', md: 'center' }}
        my={10}
      >
        <Heading mb={[3, 3, 0]} minW="30%">Users</Heading>
        <Stack direction={{ base: 'column', md: 'row' }} align="center" justify="flex-end" spacing={5} w="100%">
          <InputGroup>
            <InputLeftElement color="gray.300">
              <SearchIcon />
            </InputLeftElement>
            <Input
              value={query}
              onChange={(e: any) => setQuery(e.target.value)}
              placeholder="Search"
            />
          </InputGroup>
          <RoleFilter
            roles={roles}
            selectedRoles={selectedRoles}
            onSubmit={setSelectedRoles}
          />
          <Button colorScheme="blue" onClick={onOpen} leftIcon={<AddIcon />} minW="100px">Add User</Button>
        </Stack>
      </Flex>
      <UserListView
        loadUsers={loadUsers}
        removeUser={removeUser}
        isDeleting={isDeleting}
        query={debouncedQuery}
        refetchId={refetchId}
        selectedRoles={selectedRoles}
        currentUser={currentUser}
        operationError={operationError}
        roles={roles}
        baseUrl={`/${property.id}/businesses/${business.id}/users`}
        context="business"
        locations={locations}
      />
    </Box>
  );
};

export default BusinessUsers;
